<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n, I18nT } from 'vue-i18n';
import CloudWhatsapp from './CloudWhatsapp.vue';
import WhatsappEmbeddedSignup from './WhatsappEmbeddedSignup.vue';
import EvolutionWhatsapp from './EvolutionWhatsapp.vue';
import ChannelSelector from 'dashboard/components/ChannelSelector.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAccount } from 'dashboard/composables/useAccount';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { currentAccount } = useAccount();

const PROVIDER_TYPES = {
  WHATSAPP: 'whatsapp',
  WHATSAPP_MANUAL: 'whatsapp_manual',
  EVOLUTION: 'evolution',
};

const hasWhatsappAppId = computed(() => {
  return (
    window.chatwootConfig?.whatsappAppId &&
    window.chatwootConfig.whatsappAppId !== 'none'
  );
});

const selectedProvider = computed(() => route.query.provider);

const isWhatsappCloudEnabled = computed(() => {
  const planSettings = currentAccount.value?.custom_attributes || {};
  return planSettings.yeszap_plan_features?.includes(
    FEATURE_FLAGS.WHATSAPP_CLOUD
  );
});

const providerDescription = computed(() => {
  return isWhatsappCloudEnabled.value
    ? t('INBOX_MGMT.ADD.WHATSAPP.YESZAP_SELECT_PROVIDER.DESCRIPTION')
    : t('INBOX_MGMT.ADD.WHATSAPP.YESZAP_SELECT_PROVIDER.QR_ONLY_DESCRIPTION');
});

const availableProviders = computed(() => {
  const providers = [
    {
      key: PROVIDER_TYPES.EVOLUTION,
      title: t('INBOX_MGMT.ADD.WHATSAPP.YESZAP_PROVIDERS.EVOLUTION'),
      description: t('INBOX_MGMT.ADD.WHATSAPP.YESZAP_PROVIDERS.EVOLUTION_DESC'),
      icon: 'i-lucide-qr-code',
    },
  ];

  if (!isWhatsappCloudEnabled.value) return providers;

  return [
    {
      key: PROVIDER_TYPES.WHATSAPP,
      title: t('INBOX_MGMT.ADD.WHATSAPP.YESZAP_PROVIDERS.WHATSAPP_CLOUD'),
      description: t(
        'INBOX_MGMT.ADD.WHATSAPP.YESZAP_PROVIDERS.WHATSAPP_CLOUD_DESC'
      ),
      icon: 'i-woot-whatsapp',
    },
    ...providers,
  ];
});

const isSelectedProviderAvailable = computed(() => {
  if (!selectedProvider.value) return false;
  if (selectedProvider.value === PROVIDER_TYPES.WHATSAPP_MANUAL) {
    return isWhatsappCloudEnabled.value;
  }

  return availableProviders.value.some(
    provider => provider.key === selectedProvider.value
  );
});

const showProviderSelection = computed(() => {
  return !selectedProvider.value || !isSelectedProviderAvailable.value;
});

const showConfiguration = computed(() => {
  return Boolean(selectedProvider.value) && isSelectedProviderAvailable.value;
});

const selectProvider = providerValue => {
  router.push({
    name: route.name,
    params: route.params,
    query: { provider: providerValue },
  });
};

const shouldShowCloudWhatsapp = provider => {
  return (
    provider === PROVIDER_TYPES.WHATSAPP_MANUAL ||
    (provider === PROVIDER_TYPES.WHATSAPP && !hasWhatsappAppId.value)
  );
};

const handleManualLinkClick = () => {
  selectProvider(PROVIDER_TYPES.WHATSAPP_MANUAL);
};
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <div v-if="showProviderSelection">
      <div class="mb-10 text-left">
        <h1 class="mb-2 text-lg font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.YESZAP_SELECT_PROVIDER.TITLE') }}
        </h1>
        <p class="text-sm leading-relaxed text-n-slate-11">
          {{ providerDescription }}
        </p>
      </div>

      <div class="flex flex-wrap gap-6 justify-start">
        <ChannelSelector
          v-for="provider in availableProviders"
          :key="provider.key"
          :title="provider.title"
          :description="provider.description"
          :icon="provider.icon"
          @click="selectProvider(provider.key)"
        />
      </div>
    </div>

    <div v-else-if="showConfiguration">
      <div class="px-6 py-5 rounded-2xl border border-n-weak">
        <!-- Show embedded signup if app ID is configured -->
        <div
          v-if="
            hasWhatsappAppId && selectedProvider === PROVIDER_TYPES.WHATSAPP
          "
        >
          <WhatsappEmbeddedSignup />

          <!-- Manual setup fallback option -->
          <div class="pt-6 mt-6 border-t border-n-weak">
            <I18nT
              keypath="INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MANUAL_FALLBACK"
              tag="p"
              class="text-sm text-n-slate-11"
            >
              <template #link>
                <a
                  href="#"
                  class="underline text-n-brand"
                  @click.prevent="handleManualLinkClick"
                >
                  {{
                    $t(
                      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MANUAL_LINK_TEXT'
                    )
                  }}
                </a>
              </template>
            </I18nT>
          </div>
        </div>

        <!-- Show manual setup -->
        <EvolutionWhatsapp
          v-else-if="selectedProvider === PROVIDER_TYPES.EVOLUTION"
        />

        <CloudWhatsapp v-else-if="shouldShowCloudWhatsapp(selectedProvider)" />

        <CloudWhatsapp v-else />
      </div>
    </div>
  </div>
</template>
